import { supabase } from '@/integrations/supabase/client';

export interface ActivityLogEntry {
  id: string;
  company_id: string;
  actor_user_id: string | null;
  action: string;
  entity: string;
  entity_id: string | null;
  detail: Record<string, any> | null;
  ts: string;
  location_id: string | null;
  actor_user?: {
    id: string;
    full_name: string | null;
    email: string | null;
  };
}

export type ActivityAction = 
  | 'create' 
  | 'update' 
  | 'delete' 
  | 'view' 
  | 'export' 
  | 'print'
  | 'login'
  | 'logout'
  | 'payment'
  | 'adjustment'
  | 'convert'
  | 'start'
  | 'end'
  | 'cancel';

export type ActivityEntity = 
  | 'product'
  | 'customer'
  | 'sale'
  | 'visit'
  | 'package'
  | 'user'
  | 'location'
  | 'trade'
  | 'on_hold'
  | 'inventory_movement'
  | 'calendar_event'
  | 'notification'
  | 'company';

interface LogActivityParams {
  action: ActivityAction;
  entity: ActivityEntity;
  entityId?: string | null;
  detail?: Record<string, any>;
  locationId?: string | null;
  companyId: string;
  userId?: string | null;
}

/**
 * Log an activity to the activity_log table
 */
export async function logActivity({
  action,
  entity,
  entityId = null,
  detail = null,
  locationId = null,
  companyId,
  userId = null,
}: LogActivityParams): Promise<void> {
  try {
    // Get current user if not provided
    if (!userId) {
      const { data: { session } } = await supabase.auth.getSession();
      userId = session?.user?.id || null;
    }

    const { error } = await supabase
      .from('activity_log')
      .insert({
        company_id: companyId,
        actor_user_id: userId,
        action,
        entity,
        entity_id: entityId,
        detail: detail || null,
        location_id: locationId || null,
      });

    if (error) {
      console.error('Error logging activity:', error);
      // Don't throw - activity logging should not break the main flow
    }
  } catch (error) {
    console.error('Exception logging activity:', error);
    // Don't throw - activity logging should not break the main flow
  }
}

/**
 * Get activity logs with filters
 */
export async function getActivityLogs(params: {
  companyId: string;
  limit?: number;
  offset?: number;
  entity?: ActivityEntity;
  action?: ActivityAction;
  userId?: string;
  locationId?: string;
  startDate?: string;
  endDate?: string;
}): Promise<{ data: ActivityLogEntry[]; count: number }> {
  const {
    companyId,
    limit = 100,
    offset = 0,
    entity,
    action,
    userId,
    locationId,
    startDate,
    endDate,
  } = params;

  let query = supabase
    .from('activity_log')
    .select(`
      *,
      actor_user:user_profiles(id, full_name, email)
    `, { count: 'exact' })
    .eq('company_id', companyId)
    .order('ts', { ascending: false })
    .range(offset, offset + limit - 1);

  if (entity) {
    query = query.eq('entity', entity);
  }

  if (action) {
    query = query.eq('action', action);
  }

  if (userId) {
    query = query.eq('actor_user_id', userId);
  }

  if (locationId) {
    query = query.eq('location_id', locationId);
  }

  if (startDate) {
    query = query.gte('ts', startDate);
  }

  if (endDate) {
    query = query.lte('ts', endDate);
  }

  const { data, error, count } = await query;

  if (error) {
    throw error;
  }

  return {
    data: (data || []) as ActivityLogEntry[],
    count: count || 0,
  };
}

/**
 * Helper function to get action display name in Turkish
 */
export function getActionDisplayName(action: ActivityAction): string {
  const actionMap: Record<ActivityAction, string> = {
    create: 'Oluşturuldu',
    update: 'Güncellendi',
    delete: 'Silindi',
    view: 'Görüntülendi',
    export: 'Dışa Aktarıldı',
    print: 'Yazdırıldı',
    login: 'Giriş Yapıldı',
    logout: 'Çıkış Yapıldı',
    payment: 'Ödeme Yapıldı',
    adjustment: 'Düzenleme Yapıldı',
    convert: 'Dönüştürüldü',
    start: 'Başlatıldı',
    end: 'Sonlandırıldı',
    cancel: 'İptal Edildi',
  };
  return actionMap[action] || action;
}

/**
 * Helper function to get entity display name in Turkish
 */
export function getEntityDisplayName(entity: ActivityEntity): string {
  const entityMap: Record<ActivityEntity, string> = {
    product: 'Ürün',
    customer: 'Müşteri',
    sale: 'Satış',
    visit: 'Ziyaret',
    package: 'Paket',
    user: 'Kullanıcı',
    location: 'Lokasyon',
    trade: 'Bayi İşlemi',
    on_hold: 'Beklemede',
    inventory_movement: 'Stok Hareketi',
    calendar_event: 'Takvim Etkinliği',
    notification: 'Bildirim',
    company: 'Şirket',
  };
  return entityMap[entity] || entity;
}

